<template>
  <div
    :style="`font-family:${
      clientUserDetail?.fontFamily ? clientUserDetail.fontFamily : 'Nunito'
    };--fontColor: ${
      clientUserDetail?.colorCode?.secondaryColor
        ? clientUserDetail.colorCode.secondaryColor
        : ' #727cf5'
    };`"
    id="view"
  >
  <!-- topbar -->
   <div>

     <component
       :is="currentLayout"
       :key="layoutKey"
       :menuList="menuItems"
       @layoutToggled="handleLayout"
       @toggleMenu="handleToggleMenu"
     />
   </div>
<!-- main content -->
    <div id="content" :style="!isHorizontalLayout ? contentStyle : null">
      <div
        :class="['container-fluid', { 'layout-toggled': !isHorizontalLayout }]"
      >
        <div
          class="content-wrapper"
          :class="{ 'loading-overlay': isLoading > 0 }"
        >
          <router-view v-slot="{ Component }">
            <component :is="Component" @closeSubMenu="handleCloseSubMenu" />
          </router-view>
          <!-- Spinner, shown only when isLoading is true -->
          <div v-if="isLoading > 0" class="spinner-wrapper">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Chatbot -->
    <div v-if="!route.path.includes('/rdep-receipt/')">
      <JulienBot />
    </div>

    <Footer id="footer" />
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  watch,
  defineAsyncComponent,
  computed,
} from "vue";

import { useRoute } from "vue-router";
import JulienBot from "./JulienBot.vue";

import { isLoading } from "@/store/LoadingState";
import Footer from "@/components/Footer/FooterView.vue";

// POS stores
import { usePermissionStore } from "@/store/helper/menuPaths.js";
import { useMenuStore } from "@/store/helper/posMenuStore";

// Control Panel stores
import { useContorlPermissionStore } from "@/store/helper/controlPanelMenuPath";
import { useControlPanelMenuStore } from "@/store/helper/controlpanelMenuStore";

import { useClientUserStore } from "@/store/subDomainState";

// Async layout components
const Topbar = defineAsyncComponent(() =>
  import("@/components/Navbar/TopbarView.vue")
);
const Sidebar = defineAsyncComponent(() =>
  import("@/components/Navbar/SidebarView.vue")
);

// Reactive state
const isHorizontalLayout = ref(
  JSON.parse(localStorage.getItem("isHorizontalLayout")) ?? false
);
const sidebarOpen = ref(false);
const screenWidth = ref(window.innerWidth);
const contentStyle = ref({
  marginLeft: "50px",
  transition: "margin-left 0.3s",
});

const subDomainStore = useClientUserStore();
const clientUserDetail = ref({
  fontFamily: "",
  colorCode: {
    primaryColor: "",
    secondaryColor: "",
    backGroundColor: "",
  },
});

const menuItems = ref([]);
const layoutKey = ref(0);

const route = useRoute();

// All stores initialized (Pinia is lazy, no cost until used)
const posMenuStore = useMenuStore();
const posPermissionStore = usePermissionStore();
const controlPanelMenuStore = useControlPanelMenuStore();
const controlPanelPermissionStore = useContorlPermissionStore();

// Determine current layout
const currentLayout = computed(() =>
  isHorizontalLayout.value ? Topbar : Sidebar
);

// ✅ Check which context we're in
const isControlPanelRoute = computed(() =>
  route.path.startsWith("/control-panel")
);

const isReceiptRoute = computed(() =>
  route.path.includes("/rdep-receipt")
);

// Fetch menu based on current route context
const fetchMenuData = async () => {
  try {
    if (isControlPanelRoute.value) {
      // ✅ Control Panel route — use control panel stores
      const data = await controlPanelMenuStore.fetchMenuData();
      menuItems.value = data;
      controlPanelPermissionStore.updateAllowedPaths(data);

    } else if (!isReceiptRoute.value) {
      // ✅ POS route (not receipt, not control panel) — use POS stores
      const data = await posMenuStore.fetchMenuData();
      menuItems.value = data;
      posPermissionStore.updateAllowedPaths(data);
    }
    // Receipt routes don't need a menu fetch
  } catch (error) {
    console.error("Error fetching menu data:", error);
  }
};

// Watch route changes and fetch accordingly
watch(
  () => route.fullPath,
  () => {
    fetchMenuData();
  },
  { immediate: true }
);

// Layout toggle handler
const handleLayout = (value) => {
  isHorizontalLayout.value = value === true;
  localStorage.setItem(
    "isHorizontalLayout",
    JSON.stringify(isHorizontalLayout.value)
  );
  layoutKey.value++;
  sidebarOpen.value = false;
};

const handleToggleMenu = () => {
  sidebarOpen.value = !sidebarOpen.value;
};

const handleCloseSubMenu = () => {};

const handleResize = () => {
  screenWidth.value = window.innerWidth;
};

const updateContentStyle = () => {
  contentStyle.value = {
    marginLeft:
      screenWidth.value <= 1024 ? "0px" : !sidebarOpen.value ? "200px" : "50px",
    transition: "margin-left 0.3s",
  };
};

onMounted(() => {
  clientUserDetail.value = subDomainStore.clientUserDetail;
  fetchMenuData();
  updateContentStyle();
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

watch(sidebarOpen, updateContentStyle);
watch(screenWidth, updateContentStyle);
</script>

<style scoped>
.loading-overlay {
  pointer-events: none;
  opacity: 0.5;
  position: relative;
}

.spinner-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}
.container-fluid.layout-toggled {
  max-width: 98% !important;
}

.chatbot-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  margin: 10px;
  padding: 12px 24px;
  /* background-color: #cc007a; */
  background: linear-gradient(to right, #9433e9, #ea479a); /* Gradient */
  color: white;
  border: none;
  border-radius: 30px;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: background-color 0.3s ease;
  z-index: 998;
}

.chatbot-btn.lively {
  animation: pulse 2.5s infinite ease-in-out;
  transform-origin: center;
}

.chatbot-btn:hover {
  background: linear-gradient(to right, #ea479a, #9433e9);
  box-shadow: 0 6px 14px rgba(234, 71, 154, 0.5);
  transform: scale(1.05);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 10px rgba(148, 51, 233, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(234, 71, 154, 0.8);
  }
  100% {
    box-shadow: 0 0 10px rgba(148, 51, 233, 0.5);
  }
}

.main-chatbot {
  position: fixed;
  height: 500px;
  bottom: 15px;
  right: 20px;
  width: 800px;
  z-index: 999;
  opacity: 0;
  transform: translateY(60px);
  pointer-events: none;
  /* transition: all 0.3s ease; */
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.main-chatbot.show {
  opacity: 1;
  transform: translateY(0px);
  pointer-events: unset;
}

.main-chatbot.fullScreen {
  width: 100%;
  right: 0px;
  top: 0px;
  bottom: 0px;
  height: 100vh;
  padding: 10px;
  background-color: white;
}

/* Chatbot Panel */
.chatbot-panel {
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.backdrop {
  position: fixed;
  inset: 0;
  z-index: 990;
  background-color: transparent;
  opacity: 0.2;
}

.cursor {
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 5px;
}

.cursor:hover {
  background-color: rgba(255, 255, 255, 0.14);
}

.chatbot-header {
  background: linear-gradient(to right, #9433e9, #ea479a); /* Gradient */
  color: white;
  padding: 10px 16px;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.closeSize {
  font-size: 20px;
}

.chatbot-body {
  font-size: 14px;
  color: #333;
  display: grid;
  /* grid-template-columns: 230px 1fr; */
  overflow: hidden;
  height: 100%;
}

.chat-btn {
  background-color: white;
  border: 1px solid #3333332d;
  color: #333333;
  padding: 8px 10px;
  border-radius: 5px;
  width: 100%;
  display: flex;
  gap: 10px;
}

.hide-sidebar {
  display: none !important;
}

.show-sidebar {
  grid-column: 1/3;
}

.chat-sidebar {
  border-right: 1px solid #3333332d;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: #f9fbfc;
  /* height: 100%; */
}

.chat-conversation {
  display: grid;
  grid-template-rows: 1fr 100px;
}

.chat-section-wrapper {
  overflow-y: auto;
  max-height: 350px;
  padding: 10px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.conversation-lists {
  margin-block: 10px;
}

.conversation-list {
  margin-block: 10px;
  padding: 6px 10px;
}

.conversation-list.active {
  background-color: #e5e6eb;
  border-radius: 5px;
}

.chat-sidebar {
  padding: 10px 16px;
}

.profile-logo {
  width: 30px;
  height: 30px;
  border-radius: 30px;
}

.user-icon {
  font-size: 20px;
  padding: 6px;
  border-radius: 30px;
  background-color: #9f35dead !important;
  color: white;
}

.julien-logo {
  width: 30px;
  height: 30px;
  border-radius: 20px;
  margin-right: 10px;
}

.image-user {
  margin-top: 6px;
}

.chat-section {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.chat-section .content {
  background-color: #fcf2f8;
  padding: 12px;
  border-radius: 10px;
  font-weight: 500;
}

.content {
  border: 1px solid #8080801f;
}

.chat-section.chat-user {
  justify-content: flex-end;
}

.chat-section.chat-user .content {
  background-color: #f2f4f5;
}

.chat-options p {
  margin: 8px 0;
  color: #d0006f;
  cursor: pointer;
  text-decoration: underline;
}

/* Chatbot Input (optional) */
.chatbot-input {
  position: relative;
  padding: 10px 16px;
  border-top: 1px solid #ccccccaf;
  display: flex;
  flex: 1;
  /* margin-top: 172px; */
  width: 100%;
  max-height: 300px;
}

.button-wrapper {
  position: absolute;
  right: 26px;
  bottom: 16px;
  display: flex;
  gap: 10px;
  align-items: center;
}

.attach-file {
  font-size: 20px !important;
  color: #cb40b6 !important;
}

.chatbot-input .send-btn {
  background: linear-gradient(to right, #9433e988, #ea4788);
  border: none;
  border-radius: 5px;
  /* font-size: 18px; */
  cursor: pointer;
  color: #fff;
  padding: 6px 10px;
}

.chatbot-input textarea {
  flex: 1;
  border: none;
  outline: none;
  padding: 10px;
  font-size: 14px;
  color: #cc007a;
  border-radius: 5px;
  border: 1px solid #ccccccaf;
  font-weight: 600;
}

.chatbot-input textarea:focus {
  border: 1px solid #ff8ad0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.chatbot-input textarea::placeholder {
  color: #cc007a;
  font-weight: 600;
}

@media screen and (max-width: 1023px) {
  
  .container-fluid{
    margin-top: 3rem;
  }
}
@media screen and (min-width: 1024px) {
  .container-fluid{
    margin-top: 5.5rem;
  }
  .container-fluid.layout-toggled{
    margin-top: 3rem;
  }
}
</style>

<style>
@import url("../../style/style.css");
</style>
